import type { UseSoftphoneTransferReturn } from './index.types';
import { useNotificationStore } from 'Modules/notification/store';
import { CallManager } from 'Modules/softphone/services/CallManager';
import { EventService } from 'Modules/softphone/services/EventService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { TransferTarget } from 'Modules/softphone/types';
import { createLogger } from 'Modules/softphone/utils/logger';
import { useCallback, useEffect, useRef } from 'react';
import { pushToGTMDataLayer } from 'Utils/analytics';


const logger = createLogger('useSoftphoneTransfer');

/**
 * Hook for managing the blind transfer flow.
 *
 * Orchestrates the multi-step transfer UI (search → confirmation → execution),
 * manages auto-hold/resume of the active call, fires analytics events,
 * and prevents duplicate transfer submissions.
 *
 * @returns {UseSoftphoneTransferReturn} Transfer state and handler functions.
 *
 * @remarks
 * **Auto-Hold Behavior:**
 * When the transfer view opens, the active call is automatically placed on hold.
 * If the transfer view is closed without completing the transfer, the call remains
 * on hold — the user must explicitly resume it via the Resume button.
 *
 * **Duplicate Prevention:**
 * The hook guards against concurrent transfer submissions via the `isTransferring` flag.
 */
/**
 * Emit a diagnostic event without ever being able to affect the transfer.
 * Diagnostics observe the flow; they never participate in it.
 */
const emitTransferEvent = (
  message: string,
  details: Record<string, unknown>
): void => {
  try {
    // The Call channel: these rows say what the transfer did, while the REFER
    // itself is traced off the socket by SipService.
    EventService.getInstance().logCallEvent(message, details);
  } catch (error) {
    logger.warn('Failed to record transfer event', {
      message,
      error: (error as Error).message,
    });
  }
};

export const useSoftphoneTransfer = (): UseSoftphoneTransferReturn => {
  const transferCompletedRef = useRef(false);

  const {
    transferStep,
    transferTarget,
    transferSelectedPhoneNumber,
    isTransferring,
    transferType,
    isTransferActive,
    warmTransferContext,
    setTransferStep,
    setTransferTarget,
    setTransferSelectedPhoneNumber,
    setIsTransferring,
    resetTransferFlow,
    toggleTransfer,
    cancelTransfer,
    setWarmTransferContext,
    updateWarmTransferContext,
    clearWarmTransferContext,
  } = useSoftphoneStore();

  const callManager = CallManager.getInstance()

  /**
   * Selects a contact as the transfer target and advances to the confirmation step.
   * If the contact has a single phone number, it is auto-selected.
   *
   * @param contact - The transfer target contact selected by the user.
   */
  const handleSelectContact = useCallback((contact: TransferTarget) => {
    logger.info('Contact selected for transfer', {
      displayName: contact.displayName,
      type: contact.type,
      phoneNumberCount: contact.phoneNumbers.length,
    });

    setTransferTarget(contact);
    // Auto-select the first phone number if only one is available
    if (contact.phoneNumbers.length === 1) {
      setTransferSelectedPhoneNumber(contact.phoneNumbers[0].number);
    } else {
      setTransferSelectedPhoneNumber(null);
    }
    setTransferStep('confirmation');
  }, [setTransferTarget, setTransferSelectedPhoneNumber, setTransferStep]);

  /**
   * Selects a raw phone number as the transfer target and advances to confirmation.
   *
   * @param phoneNumber - The phone number entered or selected by the user.
   */
  const handleSelectNumber = useCallback((phoneNumber: string) => {
    logger.info('Direct number selected for transfer', { phoneNumber });

    const numberTarget: TransferTarget = {
      type: 'number',
      displayName: phoneNumber,
      phoneNumbers: [{ number: phoneNumber, label: 'Direct' }],
    };

    setTransferTarget(numberTarget);
    setTransferSelectedPhoneNumber(phoneNumber);
    setTransferStep('confirmation');
  }, [setTransferTarget, setTransferSelectedPhoneNumber, setTransferStep]);

  const handleBack = useCallback(() => {
    if (transferStep === 'confirmation') {
      logger.info('Navigating back to search');
      resetTransferFlow();
    } else {
      logger.info('Closing transfer view (back from search)');
      cancelTransfer();
    }
  }, [transferStep, cancelTransfer, resetTransferFlow]);

  const handleCancel = useCallback(() => {
    logger.info('Transfer cancelled, returning to search');
    resetTransferFlow();
  }, [resetTransferFlow]);

  /**
   * Executes a blind transfer of the active call.
   *
   * Guards against duplicate submissions, transfers the call via {@link CallManager},
   *
   * @param target - The selected transfer target (contact or number).
   * @param phoneNumber - The destination phone number to transfer to.
   * @throws Displays an error notification and attempts to resume the call on failure.
   */
  const handleBlindTransfer = useCallback(
    async (target: TransferTarget, phoneNumber: string): Promise<void> => {
      // Duplicate prevention: reject if already transferring
      if (isTransferring) {
        logger.warn('Transfer rejected: already in progress');
        return;
      }

      const state = useSoftphoneStore.getState();
      const callId = state.currentCallId;

      if (!callId) {
        logger.warn('Transfer rejected: no active call');
        return;
      }

      if (!phoneNumber) {
        logger.warn('Transfer rejected: no phone number provided');
        return;
      }

      logger.info('Initiating blind transfer', {
        callId,
        destination: phoneNumber,
        targetName: target.displayName,
      });

      setIsTransferring(true);

      try {
        await callManager.blindTransfer(callId, phoneNumber);

        // Success: mark as completed so cleanup doesn't resume the call
        transferCompletedRef.current = true;

        logger.info('Blind transfer completed successfully', { callId });
        pushToGTMDataLayer('blind_transfer_completed', {
          call_id: callId,
          destination: phoneNumber,
        });

        // Close the transfer view
        const currentState = useSoftphoneStore.getState();
        if (currentState.isTransferActive) {
          toggleTransfer();
        }
      } catch (error) {
        logger.error('Blind transfer failed', {
          callId,
          error: (error as Error).message,
        });

        pushToGTMDataLayer('blind_transfer_failed', {
          call_id: callId,
          destination: phoneNumber,
          error: (error as Error).message,
        });

        // Show error notification
        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          message: 'Transfer failed. Please try again.',
        });

        emitTransferEvent('Blind transfer failed', {
          callId,
          destination: phoneNumber,
          error: (error as Error).message,
        });

        // Resume the held call on failure
        try {
          await callManager.resumeCall(callId);
          emitTransferEvent('Call resumed after failed transfer', { callId });
        } catch (resumeError) {
          logger.error('Failed to resume call after transfer failure', {
            callId,
            error: (resumeError as Error).message,
          });
          emitTransferEvent('Resume after failed transfer also failed', {
            callId,
            error: (resumeError as Error).message,
          });
        }
      } finally {
        setIsTransferring(false);
      }
    },
    [isTransferring, callManager, toggleTransfer, setIsTransferring]
  );

  /**
   * Executes a warm (attended) transfer of the active call.
   *
   * Places the original call on hold, initiates a consultation call to the recipient,
   * and sets up the warm transfer context to track the two-call relationship.
   *
   * @param target - The selected transfer target (contact or number).
   * @param phoneNumber - The destination phone number for the consultation call.
   * @throws Displays an error notification on failure and clears warm transfer context.
   */
  const handleWarmTransfer = useCallback(
    async (target: TransferTarget, phoneNumber: string): Promise<void> => {
      // Duplicate prevention: reject if already transferring or warm transfer in progress
      if (isTransferring || warmTransferContext !== null) {
        logger.warn('Warm transfer rejected: already in progress');
        return;
      }

      const state = useSoftphoneStore.getState();
      const originalCallId = state.currentCallId;

      if (!originalCallId) {
        logger.warn('Warm transfer rejected: no active call');
        return;
      }

      logger.info('Initiating warm transfer', {
        originalCallId,
        destination: phoneNumber,
        targetName: target.displayName,
      });

      setIsTransferring(true);

      try {
        // Set warm transfer context before hold (flag set before call)
        setWarmTransferContext({
          originalCallId,
          consultationCallId: null,
          state: 'initiating',
          destination: phoneNumber,
          recipientDisplayName: target.displayName,
        });

        emitTransferEvent('Warm transfer initiating', {
          callId: originalCallId,
          destination: phoneNumber,
          recipient: target.displayName,
        });

        // Hold the original call (fail fast if hold fails)
        await callManager.holdCall(originalCallId);

        // Close transfer view if still open
        const currentState = useSoftphoneStore.getState();
        if (currentState.isTransferActive) {
          toggleTransfer();
        }

        // Make consultation call
        await callManager.makeCall(phoneNumber, target.personId);

        // Update context with consultation call ID
        const newState = useSoftphoneStore.getState();
        const consultationCallId = newState.currentCallId;
        updateWarmTransferContext({
          consultationCallId,
          state: 'consulting',
        });

        logger.info('Warm transfer initiated successfully', {
          originalCallId,
          consultationCallId,
        });
        emitTransferEvent('Warm transfer consulting', {
          callId: consultationCallId ?? originalCallId,
          originalCallId,
          consultationCallId,
          destination: phoneNumber,
        });
        // Fire analytics event
        pushToGTMDataLayer('warm_transfer_opened', {
          call_id: originalCallId,
          destination: phoneNumber,
        });
      } catch (error) {
        logger.error('Warm transfer initiation failed', {
          originalCallId,
          error: (error as Error).message,
        });

        emitTransferEvent('Warm transfer initiation failed', {
          callId: originalCallId,
          destination: phoneNumber,
          error: (error as Error).message,
        });

        clearWarmTransferContext();

        // Show error notification
        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          message: 'Warm transfer failed. Please try again.',
        });

        // Fire failure analytics event
        pushToGTMDataLayer('warm_transfer_failed', {
          call_id: originalCallId,
          destination: phoneNumber,
          error: (error as Error).message,
        });
      } finally {
        setIsTransferring(false);
      }
    },
    [
      isTransferring,
      warmTransferContext,
      callManager,
      toggleTransfer,
      setIsTransferring,
      setWarmTransferContext,
      updateWarmTransferContext,
      clearWarmTransferContext,
    ]
  );

  /**
   * Executes a voicemail transfer of the active call.
   *
   * Transfers the call directly to the recipient's voicemail using the same
   * blind transfer mechanism with a voicemail-specific destination prefix (*).
   *
   * @param target - The selected transfer target (must have hasVoicemail === true).
   * @param phoneNumber - The destination phone number to transfer to voicemail.
   * @throws Displays an error notification on failure and preserves the call in pre-transfer state.
   */
  const handleVoicemailTransfer = useCallback(
    async (target: TransferTarget, phoneNumber: string): Promise<void> => {
      // Duplicate prevention: reject if already transferring
      if (isTransferring) {
        logger.warn('Voicemail transfer rejected: already in progress');
        return;
      }

      const state = useSoftphoneStore.getState();
      const callId = state.currentCallId;

      if (!callId) {
        logger.warn('Voicemail transfer rejected: no active call');
        return;
      }

      if (target.type !== 'contact' || target.contactType !== 'directory' || target.voicemailInboxId === '0') {
        logger.warn('Voicemail transfer rejected: target does not have voicemail', {
          type: target.type,
          contactType: target.contactType,
          voicemailInboxId: target.voicemailInboxId,
        });
        return;
      }

      logger.info('Initiating voicemail transfer', {
        callId,
        destination: phoneNumber,
        targetName: target.displayName,
      });

      pushToGTMDataLayer('voicemail_transfer_opened', {
        call_id: callId,
        destination: phoneNumber,
      });

      setIsTransferring(true);

      try {
        // Voicemail transfer uses blind transfer with a * prefix on the destination
        const voicemailDestination = `*${phoneNumber}`;
        await callManager.blindTransfer(callId, voicemailDestination);

        // Success: mark as completed so cleanup doesn't resume the call
        transferCompletedRef.current = true;

        logger.info('Voicemail transfer completed successfully', { callId });
        pushToGTMDataLayer('voicemail_transfer_completed', {
          call_id: callId,
          destination: phoneNumber,
        });

        // Close the transfer view
        const currentState = useSoftphoneStore.getState();
        if (currentState.isTransferActive) {
          toggleTransfer();
        }
      } catch (error) {
        logger.error('Voicemail transfer failed', {
          callId,
          error: (error as Error).message,
        });

        pushToGTMDataLayer('voicemail_transfer_failed', {
          call_id: callId,
          destination: phoneNumber,
          error: (error as Error).message,
        });

        // Show error notification
        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          message: 'Voicemail transfer failed. Please try again.',
        });
      } finally {
        setIsTransferring(false);
      }
    },
    [isTransferring, callManager, toggleTransfer, setIsTransferring]
  );

  /**
   * @effect Auto-Hold
   *
   * When the transfer view becomes active (`isTransferActive` is `true`):
   * 1. Places the current call on hold (if not already held).
   * 2. Fires the `blind_transfer_opened` analytics event.
   * 3. Resets the `transferCompletedRef` for the new session.
   *
   * On cleanup (transfer view closed without a successful transfer):
   * - The call remains on hold so the user can explicitly resume via the Resume button.
   * - If the transfer completed successfully, no resume is attempted (the call was removed).
   */
  useEffect(() => {
    if (!isTransferActive) {
      return;
    }

    const state = useSoftphoneStore.getState();
    const callId = state.currentCallId;
    const call = callId ? state.activeCalls.get(callId) : null;

    // Auto-hold the active call when entering transfer mode
    if (call && call.state === 'active' && !call.isOnHold) {
      logger.info('Auto-holding call for transfer', { callId });
      callManager.holdCall(callId).catch((error: Error) => {
        logger.error('Failed to auto-hold call for transfer', {
          callId,
          error: error.message,
        });
      });
    }

    // Fire analytics event
    pushToGTMDataLayer('blind_transfer_opened', { call_id: callId });

    // Reset the completed ref for this transfer session
    transferCompletedRef.current = false;

    return () => {
      // On unmount (transfer closed without completing), keep the call on hold.
      // The user must explicitly resume the call via the Resume button.
      if (transferCompletedRef.current) {
        // Transfer was successful — do not resume (call was removed)
        return;
      }

      logger.info('Transfer view closed — call remains on hold for explicit resume');
    };
  }, [isTransferActive, callManager]);

  return {
    transferStep,
    transferTarget,
    selectedPhoneNumber: transferSelectedPhoneNumber,
    isTransferring,
    transferType,
    handleSelectContact,
    handleSelectNumber,
    handleBack,
    handleCancel,
    handleBlindTransfer,
    handleWarmTransfer,
    handleVoicemailTransfer,
  };
};
